'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/useT';
import { Shield, Cookie } from 'lucide-react';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const STORAGE_KEY = 'logixa_cookie_consent';

export default function CookieConsent() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Small delay for slide-up animation
        const timer = setTimeout(() => setVisible(true), 1000);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  // Re-open the banner on demand (e.g. footer "Cookie-Einstellungen" link
  // dispatches this event), so visitors can change their choice anytime.
  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener('logixa-open-cookies', open);
    return () => window.removeEventListener('logixa-open-cookies', open);
  }, []);

  const saveConsent = (settings: CookieSettings) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage not available
    }
    setVisible(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const saveSelection = () => {
    saveConsent({
      necessary: true,
      analytics,
      marketing,
      timestamp: Date.now(),
    });
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      style={{ animation: 'cookie-slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
    >
      <style>{`
        @keyframes cookie-slide-up {
          from { opacity: 0; transform: translateY(100%); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div
        className="max-w-4xl mx-auto rounded-2xl p-6 md:p-8"
        style={{
          background: 'rgba(14,14,14,0.92)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255,214,10,0.1)', color: '#FFD60A' }}>
            <Shield size={20} />
          </div>
          <div>
            <h3 className="text-base font-display font-bold text-white">{t('cookie_title')}</h3>
            <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('cookie_subtitle')}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--c-text)' }}>
          {t('cook_desc')}{' '}
          <Link href="https://logixa.one/datenschutz" className="underline transition-colors hover:text-white" style={{ color: '#FFD60A' }}>
            {t('cook_privacy_link')}
          </Link>.
        </p>

        {/* Toggles */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Necessary - always on */}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
            <div className="relative">
              <div className="w-10 h-5 rounded-full" style={{ background: 'rgba(34,197,94,0.4)' }}>
                <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full" style={{ background: '#22C55E' }} />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-white block">{t('cookie_necessary')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{t('cookie_always_on')}</span>
            </div>
          </div>

          {/* Analytics */}
          <button
            type="button"
            onClick={() => setAnalytics(!analytics)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer text-left"
            style={{
              background: analytics ? 'rgba(34,197,94,0.06)' : 'rgba(107,112,132,0.06)',
              border: `1px solid ${analytics ? 'rgba(34,197,94,0.2)' : 'rgba(107,112,132,0.15)'}`,
            }}
          >
            <div className="relative w-10 h-5 flex-shrink-0">
              <div className="w-10 h-5 rounded-full transition-colors"
                style={{ background: analytics ? 'rgba(34,197,94,0.4)' : 'rgba(107,112,132,0.3)' }}>
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                  style={{
                    background: analytics ? '#22C55E' : '#6B7084',
                    left: analytics ? 'calc(100% - 18px)' : '2px',
                  }}
                />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-white block">{t('cookie_analytics')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{analytics ? t('cook_active') : t('cook_inactive')}</span>
            </div>
          </button>

          {/* Marketing */}
          <button
            type="button"
            onClick={() => setMarketing(!marketing)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all cursor-pointer text-left"
            style={{
              background: marketing ? 'rgba(34,197,94,0.06)' : 'rgba(107,112,132,0.06)',
              border: `1px solid ${marketing ? 'rgba(34,197,94,0.2)' : 'rgba(107,112,132,0.15)'}`,
            }}
          >
            <div className="relative w-10 h-5 flex-shrink-0">
              <div className="w-10 h-5 rounded-full transition-colors"
                style={{ background: marketing ? 'rgba(34,197,94,0.4)' : 'rgba(107,112,132,0.3)' }}>
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                  style={{
                    background: marketing ? '#22C55E' : '#6B7084',
                    left: marketing ? 'calc(100% - 18px)' : '2px',
                  }}
                />
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-white block">{t('cookie_marketing')}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-muted)' }}>{marketing ? t('cook_active') : t('cook_inactive')}</span>
            </div>
          </button>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={acceptAll} className="btn-primary flex-1 text-sm text-center">
            {t('cook_accept_all')}
          </button>
          <button onClick={saveSelection} className="btn-ghost flex-1 text-sm text-center">
            {t('cook_save_selection')}
          </button>
        </div>
      </div>
    </div>
  );
}
